import React, { useCallback, useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router';

import { NexoyaPortfolioLabel } from '../types/types';

import { useLabelsQuery } from '../graphql/labels/queryLabels';
import { useLabelsFlags } from '../routes/settings/labels/useLabelsFlags';

type LabelsState = {
  labels: NexoyaPortfolioLabel[];
  labelsMap: Map<number, NexoyaPortfolioLabel>;
};

interface LabelsFilter {
  labelsFilter: NexoyaPortfolioLabel[];
  handleAddLabel: (labelFilter: NexoyaPortfolioLabel) => void;
  handleRemoveLabel: (labelFilter: NexoyaPortfolioLabel) => void;
  handleResetLabelFilterState: () => void;
}

interface LabelsContextValue {
  state: LabelsState;
  setState: React.Dispatch<React.SetStateAction<LabelsState>>;
  refetch: () => void;
  filter: LabelsFilter;
}

const LabelsContext = React.createContext<LabelsContextValue | null>(null);

function LabelsProvider(props: any) {
  const [labelsFilter, setLabelsFilter] = useState<NexoyaPortfolioLabel[]>([]);
  const [state, setState] = React.useState<LabelsState>({
    labels: [],
    labelsMap: new Map(),
  });
  const match = useRouteMatch();
  const portfolioId = parseInt(match.params.portfolioID, 10);

  const { legacyEnabled } = useLabelsFlags();
  const { data, loading, error, refetch } = useLabelsQuery({ portfolioId, skip: !legacyEnabled });
  const labels = data?.portfolioV2?.labels;

  // Eagerly reset state when portfolioId changes to prevent stale labels
  // from being visible while the new portfolio's data loads
  useEffect(() => {
    setState({ labels: [], labelsMap: new Map() });
    setLabelsFilter([]);
  }, [portfolioId]);

  useEffect(() => {
    if (!legacyEnabled) {
      setState({ labels: [], labelsMap: new Map() });
      setLabelsFilter([]);
      return;
    }
    if (!loading && data?.portfolioV2) {
      setState({
        labels: labels ?? [],
        labelsMap: new Map((labels ?? []).map((label) => [label.labelId, label])),
      });
    }
  }, [legacyEnabled, labels, loading, data?.portfolioV2]);

  const handleAddLabel = useCallback(
    (labelFilter: NexoyaPortfolioLabel) => {
      setLabelsFilter((prevState) => [...prevState, labelFilter]);
    },
    [setLabelsFilter],
  );

  const handleRemoveLabel = useCallback(
    (labelFilter: NexoyaPortfolioLabel) => {
      setLabelsFilter((prevState) => [...prevState.filter((f) => f.labelId !== labelFilter.labelId)]);
    },
    [setLabelsFilter],
  );

  const handleResetLabelFilterState = useCallback(() => {
    setLabelsFilter([]);
  }, [setLabelsFilter]);

  if (error) {
    // eslint-disable-next-line no-console
    console.log(error);
  }

  const values = React.useMemo(
    () => ({
      state,
      setState,
      refetch,
      filter: {
        labelsFilter,
        handleAddLabel,
        handleRemoveLabel,
        handleResetLabelFilterState,
      },
    }),
    [state, setState, refetch, labelsFilter, handleAddLabel, handleRemoveLabel, handleResetLabelFilterState],
  );

  return <LabelsContext.Provider value={values} {...props} />;
}

function withLabelsProvider(Component: any) {
  return (props: any) => (
    <LabelsProvider>
      <Component {...props} />
    </LabelsProvider>
  );
}

function useLabels() {
  const context = React.useContext(LabelsContext);

  if (context == null) {
    throw new Error(`useLabels must be used within LabelsProvider`);
  }

  const {
    state,
    refetch,
    filter: { labelsFilter, handleAddLabel, handleRemoveLabel, handleResetLabelFilterState },
  } = context;

  const labelById = React.useCallback(
    (labelId: number): NexoyaPortfolioLabel | undefined => {
      const label = state.labelsMap.get(labelId);

      if (!label) {
        // eslint-disable-next-line no-console
        console.warn(`useLabels: we could not find such label (labelId:${labelId})`);
        return undefined;
      }

      return label;
    },
    [state],
  );

  return {
    ...state,
    labelById,
    refetch,
    filter: {
      labelsFilter,
      handleAddLabel,
      handleRemoveLabel,
      handleResetLabelFilterState,
    },
  };
}

export { LabelsProvider, withLabelsProvider, useLabels };
